import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList
} from './command';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from './popover';

export interface PeriodInputProps {
	value?: number; // Always in minutes
	onChange?: (minutes: number) => void;
	disabled?: boolean;
	className?: string;
	'aria-invalid'?: boolean;
}

interface PeriodOption {
	minutes: number;
	labelKey: string;
	searchTerms: string[];
}

/**
 * PeriodInput - Autocomplete period input component
 *
 * - Stores and emits values in minutes
 * - Provides predefined period options with autocomplete
 * - Supports free-form numeric input (treated as minutes)
 * - Localized display strings
 */
	export const PeriodInput = React.forwardRef<HTMLDivElement, PeriodInputProps>(
		({ value = 0, onChange, disabled = false, className, 'aria-invalid': ariaInvalid }, ref) => {
	const { t } = useTranslation();
	const [open, setOpen] = React.useState(false);
	const [inputValue, setInputValue] = React.useState('');
	const [isFocused, setIsFocused] = React.useState(false);
	const [suggestion, setSuggestion] = React.useState('');
	const [highlightedIndex, setHighlightedIndex] = React.useState<number>(0);
	const justSelectedRef = React.useRef(false);		// Predefined period options
		const periodOptions: PeriodOption[] = React.useMemo(() => [
			{ minutes: 5, labelKey: 'period.5min', searchTerms: ['5', 'five', 'minute'] },
			{ minutes: 10, labelKey: 'period.10min', searchTerms: ['10', 'ten', 'minute'] },
			{ minutes: 15, labelKey: 'period.15min', searchTerms: ['15', 'fifteen', 'quarter', 'minute'] },
			{ minutes: 30, labelKey: 'period.30min', searchTerms: ['30', 'thirty', 'half', 'minute'] },
			{ minutes: 45, labelKey: 'period.45min', searchTerms: ['45', 'forty', 'minute'] },
			{ minutes: 60, labelKey: 'period.1hour', searchTerms: ['60', '1', 'one', 'hour'] },
			{ minutes: 90, labelKey: 'period.1.5hour', searchTerms: ['90', '1.5', 'one half', 'hour'] },
			{ minutes: 120, labelKey: 'period.2hour', searchTerms: ['120', '2', 'two', 'hour'] },
			{ minutes: 180, labelKey: 'period.3hour', searchTerms: ['180', '3', 'three', 'hour'] },
			{ minutes: 240, labelKey: 'period.4hour', searchTerms: ['240', '4', 'four', 'hour'] },
			{ minutes: 300, labelKey: 'period.5hour', searchTerms: ['300', '5', 'five', 'hour'] },
			{ minutes: 360, labelKey: 'period.6hour', searchTerms: ['360', '6', 'six', 'hour'] },
			{ minutes: 420, labelKey: 'period.7hour', searchTerms: ['420', '7', 'seven', 'hour'] },
			{ minutes: 480, labelKey: 'period.8hour', searchTerms: ['480', '8', 'eight', 'hour'] },
			{ minutes: 540, labelKey: 'period.9hour', searchTerms: ['540', '9', 'nine', 'hour'] },
			{ minutes: 600, labelKey: 'period.10hour', searchTerms: ['600', '10', 'ten', 'hour'] },
			{ minutes: 660, labelKey: 'period.11hour', searchTerms: ['660', '11', 'eleven', 'hour'] },
			{ minutes: 720, labelKey: 'period.12hour', searchTerms: ['720', '12', 'twelve', 'hour'] }
		], []);

		// Get localized label for a period option
		const getOptionLabel = React.useCallback((option: PeriodOption): string => {
			// Fallback display if translation missing
			const fallback = option.minutes < 60
				? `${option.minutes} ${t('common.minutes', 'Minutes')}`
				: option.minutes === 90
					? `1.5 ${t('common.hours', 'Hours')}`
					: `${option.minutes / 60} ${option.minutes / 60 === 1 ? t('common.hour', 'Hour') : t('common.hours', 'Hours')}`;

			return t(option.labelKey, fallback);
		}, [t]);

		// Get display text for current value
		const getDisplayText = React.useCallback((minutes: number): string => {
			const option = periodOptions.find(opt => opt.minutes === minutes);
			if (option) {
				return getOptionLabel(option);
			}
			// Custom value - display as minutes
			return `${minutes} ${t('common.minutes', 'Minutes')}`;
		}, [periodOptions, getOptionLabel, t]);

	// Parse user input to minutes
	const parseInput = (input: string): number | null => {
		const trimmed = input.trim();
		if (!trimmed) return null;

		// First try to parse as a number (treat as minutes)
		const numericValue = parseFloat(trimmed);
		if (!isNaN(numericValue) && numericValue >= 0) {
			return Math.round(numericValue);
		}

		// If not a number, check if it matches any predefined option search terms
		const lowerInput = trimmed.toLowerCase();
		const matchedOption = periodOptions.find(opt =>
			opt.searchTerms.some(term => lowerInput.includes(term.toLowerCase()))
		);

		if (matchedOption) {
			return matchedOption.minutes;
		}

		return null;
	};	// Handle selection from dropdown
	const handleSelect = (minutes: number) => {
		justSelectedRef.current = true;
		onChange?.(minutes);
		setOpen(false);
		setInputValue('');
		setTimeout(() => {
			justSelectedRef.current = false;
		}, 300);
	};	// Handle blur - finalize any pending input
	const handleBlur = () => {
		// Don't process blur if we just selected an item
		if (justSelectedRef.current) {
			return;
		}
		if (inputValue) {
			const parsed = parseInput(inputValue);
			if (parsed !== null) {
				onChange?.(parsed);
			}
		} else {
			// Empty input - clear the value
			onChange?.(0);
		}
		setInputValue('');
	};

	// Check if current input represents a custom value (not in predefined options)
	const hasCustomOption = React.useCallback(() => {
		if (!inputValue) return false;
		const parsed = parseInput(inputValue);
		if (parsed === null) return false;
		return !periodOptions.find(opt => opt.minutes === parsed);
	}, [inputValue, parseInput, periodOptions]);

	// Always show all options (no filtering)
	const filteredOptions = React.useMemo(() => {
		return periodOptions;
	}, [periodOptions]);

	// Set highlighted index to current value option when opening or when input changes
	React.useEffect(() => {
		if (open) {
			if (inputValue) {
				// Find matching option based on typed input
				const lowerInput = inputValue.toLowerCase();
				const matchIndex = periodOptions.findIndex(option => {
					const label = getOptionLabel(option).toLowerCase();
					return label.startsWith(lowerInput) ||
						   option.searchTerms.some(term => term.toLowerCase().startsWith(lowerInput)) ||
						   option.minutes.toString() === inputValue;
				});

				if (matchIndex !== -1) {
					setHighlightedIndex(matchIndex);
				} else {
					// No match found - check if there's a custom option and highlight it
					if (hasCustomOption()) {
						setHighlightedIndex(filteredOptions.length); // Custom option is at the end
					} else {
						setHighlightedIndex(0);
					}
				}
			} else if (value) {
				// No input, highlight current value
				const currentIndex = periodOptions.findIndex(opt => opt.minutes === value);
				if (currentIndex !== -1) {
					setHighlightedIndex(currentIndex);
				} else {
					setHighlightedIndex(0);
				}
			}
		}
	}, [open, value, inputValue, periodOptions, getOptionLabel]);

	// Scroll highlighted item into view
	React.useEffect(() => {
		if (open && highlightedIndex >= 0) {
			// Use a small delay to ensure the DOM is updated
			requestAnimationFrame(() => {
				const highlightedElement = document.querySelector(`[data-highlighted="true"]`);
				if (highlightedElement) {
					highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
				}
			});
		}
	}, [highlightedIndex, open, inputValue]); // Added inputValue to re-run when custom option appears

	// Update suggestion based on typed input (using useEffect to avoid infinite loops)
	React.useEffect(() => {
		if (!inputValue) {
			setSuggestion('');
			return;
		}

		// Find matching option based on input
		const lowerInput = inputValue.toLowerCase();
		const matchingOption = periodOptions.find(option => {
			const label = getOptionLabel(option).toLowerCase();
			return label.startsWith(lowerInput) ||
				   option.searchTerms.some(term => term.toLowerCase().startsWith(lowerInput));
		});

		if (matchingOption) {
			const optionLabel = getOptionLabel(matchingOption);
			const lowerOption = optionLabel.toLowerCase();

			// Check if the option starts with what the user typed
			if (lowerOption.startsWith(lowerInput)) {
				// Find where the match ends to preserve exact spacing
				const matchLength = inputValue.length;
				const completion = optionLabel.substring(matchLength);
				setSuggestion(inputValue + completion);
			} else {
				setSuggestion(optionLabel);
			}
		} else {
			// If no match but valid number, suggest as minutes
			const parsed = parseInput(inputValue);
			if (parsed !== null) {
				const minutesSuggestion = `${inputValue} ${t('common.minutes', 'Minutes')}`;
				setSuggestion(minutesSuggestion);
			} else {
				setSuggestion('');
			}
		}
	}, [inputValue, periodOptions, getOptionLabel, parseInput, t]);	const inputRef = React.useRef<HTMLInputElement>(null);
	const listboxId = React.useId();

	// Get current display value for the input
	const currentDisplayValue = React.useMemo(() => {
		// When focused, show what user is typing (allow empty string)
		if (isFocused) {
			return inputValue !== undefined ? inputValue : '';
		}
		// When not focused, show formatted value
		return value ? getDisplayText(value) : '';
	}, [isFocused, inputValue, value, getDisplayText]);

	// Handle input focus
	const handleFocus = () => {
		setIsFocused(true);
		// Set input to user-friendly numeric value (hours if divisible by 60, otherwise minutes)
		if (value) {
			const displayValue = value % 60 === 0 ? (value / 60).toString() : value.toString();
			setInputValue(displayValue);
		}
		setSuggestion('');
		// Open dropdown to show available options with a small delay to prevent jitter
		setTimeout(() => {
			setOpen(true);
		}, 10);
		// Select all text so typing replaces it
		setTimeout(() => {
			inputRef.current?.select();
		}, 20);
	};

	// Handle input key down
	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Escape') {
			setOpen(false);
			setInputValue('');
			setSuggestion('');
			inputRef.current?.blur();
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (!open) {
				setOpen(true);
			} else {
				// Get total options including custom option if present
				const totalOptions = filteredOptions.length + (hasCustomOption() ? 1 : 0);
				if (totalOptions > 0) {
					setHighlightedIndex((prev) => (prev + 1) % totalOptions);
				}
			}
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (!open) {
				setOpen(true);
			} else {
				// Get total options including custom option if present
				const totalOptions = filteredOptions.length + (hasCustomOption() ? 1 : 0);
				if (totalOptions > 0) {
					setHighlightedIndex((prev) => (prev - 1 + totalOptions) % totalOptions);
				}
			}
		} else if ((e.key === 'Enter' || e.key === 'Tab') && open) {
			// Select highlighted option when dropdown is open
			e.preventDefault();
			if (filteredOptions.length > 0 && highlightedIndex < filteredOptions.length) {
				handleSelect(filteredOptions[highlightedIndex].minutes);
			} else if (inputValue) {
				// Custom value
				const parsed = parseInput(inputValue);
				if (parsed !== null) {
					handleSelect(parsed);
				}
			}
			if (e.key !== 'Tab') {
				inputRef.current?.blur();
			}
		} else if (e.key === 'ArrowRight' && suggestion && isFocused) {
			// Accept the suggestion with ArrowRight
			e.preventDefault();
			if (filteredOptions.length > 0 && highlightedIndex < filteredOptions.length) {
				handleSelect(filteredOptions[highlightedIndex].minutes);
			} else if (inputValue) {
				const parsed = parseInput(inputValue);
				if (parsed !== null) {
					handleSelect(parsed);
				}
			}
			inputRef.current?.blur();
		} else if (e.key === 'Enter' && inputValue) {
			e.preventDefault();
			const parsed = parseInput(inputValue);
			if (parsed !== null) {
				onChange?.(parsed);
				setOpen(false);
				setInputValue('');
				setSuggestion('');
				inputRef.current?.blur();
			}
		}
	};

	return (
		<div ref={ref} className={cn('relative', className)}>
			<Popover open={open} onOpenChange={setOpen} modal={false}>
				<PopoverAnchor asChild>
					<div className="relative">
					{/* Suggestion ghost text behind input */}
					{isFocused && suggestion && inputValue && (
						<div className="absolute inset-0 flex items-center pointer-events-none pl-3">
							<span className="text-sm text-transparent select-none">{inputValue}</span>
							<span className="text-sm text-muted-foreground/50 select-none whitespace-pre">{suggestion.substring(inputValue.length)}</span>
						</div>
					)}
					{/* eslint-disable-next-line jsx-a11y/aria-proptypes */}
					<input
						ref={inputRef}
						type="text"
						role="combobox"
						aria-haspopup="listbox"
						aria-expanded={open}
						aria-controls={listboxId}
						aria-label={t('period.selectPeriod', 'Select period...')}
						disabled={disabled}
						placeholder={t('period.selectPeriod', 'Select period...')}
					value={currentDisplayValue}
				onChange={(e) => {
					const rawValue = e.target.value;
					// Only allow numeric input (0-9) when focused
					const newValue = isFocused ? rawValue.replace(/[^0-9]/g, '') : rawValue;
					setInputValue(newValue);
					// Open dropdown when user starts typing
					if (newValue && !open) {
						setOpen(true);
					}
				}}
						onFocus={handleFocus}
						onKeyDown={handleKeyDown}
						onBlur={() => {
							// Delay to allow click on dropdown items
							setTimeout(() => {
								setIsFocused(false);
								setSuggestion('');
								handleBlur();
							}, 200);
						}}
						className={cn(
							'flex h-9 w-full rounded-md border border-input bg-[#fafafa] dark:bg-slate-900 px-3 py-2 text-sm font-semibold shadow-xs ring-offset-background transition-colors',
							'placeholder:text-placeholder placeholder:font-light placeholder-shown:font-normal',
							'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
							'disabled:cursor-not-allowed disabled:opacity-50',
							ariaInvalid && 'border-destructive'
						)}
					/>
					<PopoverTrigger asChild>
						<button
							type="button"
							tabIndex={-1}
							disabled={disabled}
							aria-label={t('period.toggleSuggestions', 'Toggle suggestions')}
							className="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
							onClick={() => {
								setOpen(!open);
								inputRef.current?.focus();
							}}
						>
							<svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<path d="m6 9 6 6 6-6"/>
							</svg>
							</button>
						</PopoverTrigger>
					</div>
				</PopoverAnchor>
				<PopoverContent
					className="w-[var(--radix-popover-trigger-width)] p-0"
					align="start"
					onOpenAutoFocus={(e) => e.preventDefault()}
					onWheel={(e) => e.stopPropagation()}
				>
					<Command shouldFilter={false} id={listboxId}>
							<CommandList className="overscroll-contain">
					<CommandGroup>
						{filteredOptions.map((option, index) => (
							<CommandItem
								key={option.minutes}
								value={option.minutes.toString()}
								onSelect={() => handleSelect(option.minutes)}
								data-highlighted={highlightedIndex === index}
								className={cn(highlightedIndex === index && 'bg-accent')}
							>
								<Check
									className={cn(
										'mr-2 h-4 w-4',
										value === option.minutes ? 'opacity-100' : 'opacity-0'
									)}
								/>
								{getOptionLabel(option)}
							</CommandItem>
						))}
						</CommandGroup>
						{(() => {
							if (!inputValue) return null;
							const parsed = parseInput(inputValue);
							if (parsed === null) return null;
							// Show custom option if parsed value doesn't match any predefined option
							const existingOption = periodOptions.find(opt => opt.minutes === parsed);
							if (existingOption) return null;

							const isCustomHighlighted = highlightedIndex === filteredOptions.length;
							return (
								<CommandGroup>
									<CommandItem
										key="custom"
										value="custom"
										onSelect={() => handleSelect(parsed)}
										data-highlighted={isCustomHighlighted}
										data-custom-option="true"
										className={cn(isCustomHighlighted && 'bg-accent')}
									>
										<Check className="mr-2 h-4 w-4 opacity-0" />
										{inputValue} {t('common.minutes', 'Minutes')}
									</CommandItem>
								</CommandGroup>
							);
						})()}
						{filteredOptions.length === 0 && (!inputValue || parseInput(inputValue) === null) && (
							<CommandEmpty>
								{t('period.noResults', 'No periods found.')}
							</CommandEmpty>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
			</Popover>
		</div>
	);
}
);

PeriodInput.displayName = 'PeriodInput';
